class CartNotification extends HTMLElement {
  constructor() {
    super();
    this.notification = this.querySelector('#cart-notification');
    this.heading = this.querySelector('[data-cart-heading]');
    this.checkmark = this.querySelector('.icon-checkmark');
    this.overlay = this.querySelector('[data-cart-overlay]');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.notification.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
    this.querySelectorAll('button[type="button"]').forEach((button) =>
      button.addEventListener('click', () => this.close())
    );
    this.headerClick = (event) => {
      const link = event.target.closest('#cart-icon-bubble');
      if (!link || !document.querySelector('abc-product-bottom-action') || event.defaultPrevented
        || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.openCart(link);
    };
    document.addEventListener('click', this.headerClick);
    this.reviewVersion = 0;
    window.addEventListener('pagehide', () => {
      this.reviewVersion += 1;
      this.reviewController?.abort();
      this.reviewPromise = null;
      this.close(false);
    });
  }

  open() {
    if (this.parentElement !== document.body) document.body.appendChild(this);
    this.classList.add('abc-cart-floating');
    this.overlay.hidden = false;
    if (!this.notification.classList.contains('active')) {
      this.background = Array.from(document.body.children)
        .filter((element) => element !== this && !element.inert && !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName));
      this.background.forEach((element) => { element.inert = true; });
    }
    this.notification.classList.add('animate', 'active');
    document.body.classList.add('abc-cart-modal-open');
    const focusTarget = document.activeElement === this.notification
      ? this.querySelector('.cart-notification__close') : this.notification;
    trapFocus(this.notification, focusTarget);
    document.body.addEventListener('click', this.onBodyClick);
  }

  close(restoreFocus = true) {
    if (!this.notification.classList.contains('active')) return;
    this.notification.classList.remove('active');
    this.classList.remove('abc-cart-floating');
    this.overlay.hidden = true;
    document.body.classList.remove('abc-cart-modal-open');
    this.background?.forEach((element) => { element.inert = false; });
    this.background = [];
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus();
    if (restoreFocus && this.activeElement?.isConnected) this.activeElement.focus({ preventScroll: true });
  }

  setMode(review, empty = false) {
    this.notification.classList.toggle('abc-cart-review', review);
    this.notification.classList.toggle('abc-cart-empty', empty);
    const title = review ? this.dataset.cartTitle : this.dataset.addedTitle;
    this.notification.setAttribute('aria-label', title);
    if (this.heading) this.heading.textContent = title;
    if (this.checkmark) this.checkmark.style.display = review ? 'none' : '';
    this.querySelector('#cart-notification-form').hidden = empty;
    const view = this.querySelector('#cart-notification-button');
    const checkout = this.querySelector('#cart-notification-form button');
    view.classList.toggle('button--primary', review);
    view.classList.toggle('button--secondary', !review);
    checkout.classList.toggle('button--primary', !review);
    checkout.classList.toggle('button--secondary', review);
  }

  async openCart(trigger) {
    if (this.reviewPromise) return this.reviewPromise;
    this.setActiveElement(trigger);
    const controller = new AbortController();
    this.reviewController = controller;
    const version = ++this.reviewVersion;
    const timeout = setTimeout(() => controller.abort(), 8000);
    trigger?.setAttribute('aria-busy', 'true');
    this.reviewPromise = (async () => {
      try {
        const response = await fetch(`${this.dataset.cartUrl}.js`, {
          method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('cart');
        const cart = await response.json();
        if (!this.isConnected || version !== this.reviewVersion) return;
        this.renderCartReview(cart);
        this.open();
      } catch (_) {
        if (this.isConnected && version === this.reviewVersion) window.location.assign(this.dataset.cartUrl);
      } finally {
        clearTimeout(timeout);
        trigger?.removeAttribute('aria-busy');
        if (version === this.reviewVersion) {
          this.reviewPromise = null;
          this.reviewController = null;
        }
      }
    })();
    return this.reviewPromise;
  }

  renderCartReview(cart) {
    if (!Array.isArray(cart.items) || !/^[A-Z]{3}$/.test(cart.currency || '')
      || !Number.isInteger(cart.item_count) || cart.item_count < 0) throw new Error('cart');
    const fragment = document.createDocumentFragment();
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: cart.currency, currencyDisplay: 'code',
    });
    const text = (tag, className, value) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      node.textContent = value;
      return node;
    };
    let count = 0;
    cart.items.forEach((item) => {
      if (!Number.isInteger(item.quantity) || item.quantity < 1 || typeof item.product_title !== 'string'
        || !Number.isInteger(item.final_line_price) || item.final_line_price < 0) throw new Error('cart item');
      count += item.quantity;
      const row = document.createElement('div');
      row.className = 'cart-item';
      const media = document.createElement('div');
      media.className = 'cart-notification-product__image global-media-settings';
      const template = Array.from(this.querySelectorAll('[data-cart-media]'))
        .find((entry) => entry.dataset.cartMedia === item.handle);
      if (template) media.appendChild(template.content.cloneNode(true));
      else if (item.image) {
        const url = new URL(item.image, window.location.origin);
        if (url.protocol !== 'https:') throw new Error('cart image');
        const image = document.createElement('img');
        image.src = url.toString();
        image.alt = item.featured_image?.alt || item.product_title;
        image.width = 70;
        image.loading = 'lazy';
        media.appendChild(image);
      }
      const info = document.createElement('div');
      info.appendChild(text('h3', 'cart-notification-product__name h4', item.product_title));
      const options = document.createElement('dl');
      const option = (name, value) => {
        const pair = document.createElement('div');
        pair.className = 'product-option';
        pair.append(text('dt', '', `${name}: `), text('dd', '', String(value)));
        options.appendChild(pair);
      };
      if (!item.product_has_only_default_variant) (item.options_with_values || []).forEach((entry) => option(entry.name, entry.value));
      Object.entries(item.properties || {}).forEach(([name, value]) => {
        const madeToOrder = ['rose', 'black-rose-hoodie'].includes(item.handle) && ['MADE TO ORDER', '受注生産'].includes(name);
        if (value && !name.startsWith('_') && !madeToOrder) option(name, value);
      });
      info.appendChild(options);
      if (item.selling_plan_allocation?.selling_plan?.name) info.appendChild(text('p', 'product-option', item.selling_plan_allocation.selling_plan.name));
      info.appendChild(text('p', 'abc-cart-review-quantity',
        `${this.dataset.quantityLabel}: ${item.quantity} · ${money.format(item.final_line_price / 100)}`));
      row.append(media, info);
      fragment.appendChild(row);
    });
    if (count !== cart.item_count) throw new Error('cart count');
    if (!count) fragment.appendChild(text('p', 'abc-cart-review-empty', this.dataset.emptyLabel));
    this.querySelector('#cart-notification-product').replaceChildren(fragment);
    const label = this.dataset.viewLabel.replace('[count]', String(count));
    this.querySelector('#cart-notification-button').textContent = label;
    const icon = document.getElementById('cart-icon-bubble');
    if (icon) {
      icon.setAttribute('aria-label', label);
      let bubble = icon.querySelector('.cart-count-bubble');
      if (count && !bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        icon.appendChild(bubble);
      }
      if (bubble) {
        bubble.hidden = !count;
        bubble.textContent = String(count);
        bubble.setAttribute('aria-hidden', 'true');
      }
    }
    this.setMode(true, count === 0);
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
    }
  }

  renderContents(parsedState) {
    this.reviewVersion += 1;
    this.reviewController?.abort();
    this.reviewPromise = null;
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id], section.selector
      );
    });
    document.getElementById('cart-icon-bubble')?.removeAttribute('aria-label');
    this.setMode(false);
    this.open();
  }

  getSectionsToRender() {
    return [
      { id: 'cart-notification-product', selector: `[id="cart-notification-product-${this.cartItemKey}"]` },
      { id: 'cart-notification-button' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string') throw new Error('cart section');
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!section) throw new Error(`Cart section selector missing: ${selector}`);
    return section.innerHTML;
  }

  handleBodyClick(event) {
    if (!this.notification.contains(event.target)) this.close();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
